/**
 * @fileoverview Card wrapper for one Job Settings group with its own Save.
 *
 * Purpose: Job settings fields are grouped into sections so a practitioner can
 * save the block they just edited without scrolling to a single page-level
 * button. Saves stay explicit (not autosave) because these fields feed
 * documents, searches, and the conflict check.
 *
 * Used by: Job Details, Appointment Details, and Document Setup tabs.
 */

import type { ReactNode } from "react";
import { Loader2 } from "lucide-react";
import { Badge } from "@/ui/primitives/badge";
import { Button } from "@/ui/primitives/button";
import {
  Card,
  CardContent,
  CardDescription,
  CardFooter,
  CardHeader,
  CardTitle,
} from "@/ui/primitives/card";

interface JobSettingsSectionProps {
  title: string;
  description?: string;
  children: ReactNode;
  /** Extra controls in the header (e.g. "Add address"). */
  actions?: ReactNode;
  canEdit: boolean;
  isDirty: boolean;
  isSaving: boolean;
  /**
   * Blocks Save while the section's fields are invalid (e.g. the bankruptcy
   * case record's dates are internally inconsistent — MOR-661 QA-05).
   * Discard stays enabled so the practitioner can back out.
   */
  saveDisabled?: boolean;
  onSave: () => void;
  onDiscard: () => void;
}

/**
 * One settings group with an always-visible Save in the footer.
 *
 * @component
 * @example
 * <JobSettingsSection
 *   title="Bank details"
 *   isDirty={bankHasChanges}
 *   isSaving={savingSection === "bank"}
 *   canEdit={isOwnerOrMember}
 *   onSave={handleSaveBank}
 *   onDiscard={handleDiscardBank}
 * >
 *   <Input />
 * </JobSettingsSection>
 */
export function JobSettingsSection({
  title,
  description,
  children,
  actions,
  canEdit,
  isDirty,
  isSaving,
  saveDisabled = false,
  onSave,
  onDiscard,
}: JobSettingsSectionProps) {
  return (
    <Card>
      <CardHeader className="flex flex-row items-start justify-between gap-3 space-y-0 p-4">
        <div className="flex min-w-0 flex-col gap-1">
          <div className="flex flex-wrap items-center gap-2">
            <CardTitle className="text-sm font-semibold leading-none">
              {title}
            </CardTitle>
            {isDirty ? (
              <Badge color="amber" variant="soft" size="sm">
                Unsaved
              </Badge>
            ) : null}
          </div>
          {description ? (
            <CardDescription className="text-xs">{description}</CardDescription>
          ) : null}
        </div>
        {actions ? <div className="shrink-0">{actions}</div> : null}
      </CardHeader>
      <CardContent className="flex flex-col gap-4 p-4 pt-0">
        {children}
      </CardContent>
      {canEdit && isDirty ? (
        <CardFooter className="justify-end gap-1 border-t bg-amber-50/80 px-4 py-2 dark:bg-amber-950/20">
          <Button
            type="button"
            variant="ghost"
            size="sm"
            className="h-7 px-2 text-xs"
            onClick={onDiscard}
            disabled={isSaving}
          >
            Discard
          </Button>
          <Button
            type="button"
            size="sm"
            className="h-7 gap-1.5 px-3 text-xs"
            onClick={onSave}
            disabled={isSaving || saveDisabled}
          >
            {isSaving ? <Loader2 className="h-3 w-3 animate-spin" /> : null}
            Save
          </Button>
        </CardFooter>
      ) : null}
    </Card>
  );
}
